/* eslint-disable @typescript-eslint/no-unused-vars */
import {
  HotTokensRowInfo,
  HotTokensSortKeys,
} from 'api/types/httpsTypes/contracts';
import TokenColumn from './columnsComponent/tokenColumn';
import PriceColumn from './columnsComponent/priceColumn';
import NetFlowColumn from './columnsComponent/netFlowColumn';
import VolatilityColumn from './columnsComponent/volatilityColumn';
import VolumeColumn from './columnsComponent/volumeColumn';
import MarketCapColumn from './columnsComponent/marketcapColumn';
import LiquidityColumn from './columnsComponent/liquidityColumn';
import LaunchColumn from './columnsComponent/launchColumn';
import MobileColumn from './columnsComponent/mobileColumn';
import { ActionsColumn } from './columnsComponent/actionColumn';
import { TokenStatsWebSocketShortMessage } from 'api/hooks/useTokenStats';
import { HotTokensRowColumn } from './TableRow';
import { HotTokensPeriods } from '../Wallets/usePageParams';
import Tooltip from 'modules/shared-components/tooltip';

export interface ColumnsProps {
  data: HotTokensRowInfo;
  tokenStats: TokenStatsWebSocketShortMessage;
  period: HotTokensPeriods;
}

export const desktopColumns = (): HotTokensRowColumn<HotTokensRowInfo>[] => {
  return [
    {
      label: (
        <div className="xxs:text-dex-white-secondary hover:text-dex-white">
          <Tooltip text="The Token address, chain, and DEX.">
            <div>Token</div>
          </Tooltip>
        </div>
      ),
      key: 'TOKEN',
      component: TokenColumn as ({
        data,
        tokenStats,
      }: {
        data: HotTokensRowInfo;
        tokenStats: TokenStatsWebSocketShortMessage | undefined | null;
      }) => JSX.Element,
    },
    {
      label: (
        <div className="xxs:text-dex-white-secondary hover:text-dex-white">
          <Tooltip text={`The current price and the price change.`}>
            <div>Price</div>
          </Tooltip>
        </div>
      ),
      key: 'PRICE',
      sortableKey: HotTokensSortKeys.PriceChange,
      component: PriceColumn as ({
        data,
        tokenStats,
      }: {
        data: HotTokensRowInfo;
        tokenStats: TokenStatsWebSocketShortMessage | undefined | null;
      }) => JSX.Element,
    },
    {
      label: (
        <div className="xxs:text-dex-white-secondary hover:text-dex-white">
          <Tooltip
            text={`The net amount of money in and out from smart wallets and all wallets in the selected period. Hover to get more information about buy and sell from smart wallets / all wallets.`}
          >
            <div>Smart Money</div>
          </Tooltip>
        </div>
      ),
      key: 'NET_FLOW',
      sortableKey: HotTokensSortKeys.SmartFlow,
      component: NetFlowColumn as ({
        data,
        tokenStats,
      }: {
        data: HotTokensRowInfo;
        tokenStats: TokenStatsWebSocketShortMessage | undefined | null;
      }) => JSX.Element,
    },
    {
      label: (
        <div className="xxs:text-dex-white-secondary hover:text-dex-white">
          <Tooltip
            text={`The volatility index is calculated using the coefficient of variation, which is the ratio of the standard deviation of returns to the average returns over a given period. A higher volatility index indicates greater price fluctuations. This index can be used to compare volatility across different tokens, making it a useful metric for evaluating relative price volatility.`}
          >
            <div>Volatility Index</div>
          </Tooltip>
        </div>
      ),
      key: 'VOLATILITY_INDEX',
      sortableKey: HotTokensSortKeys.VolatilityIndex,
      component: VolatilityColumn as ({
        data,
        tokenStats,
      }: {
        data: HotTokensRowInfo;
        tokenStats: TokenStatsWebSocketShortMessage | undefined | null;
      }) => JSX.Element,
    },
    {
      label: (
        <div className="xxs:text-dex-white-secondary hover:text-dex-white">
          <Tooltip text={`The total trading volume in the selected period.`}>
            <div>Volume</div>
          </Tooltip>
        </div>
      ),
      key: 'VOLUME',
      sortableKey: HotTokensSortKeys.Volume,
      component: VolumeColumn as ({
        data,
        tokenStats,
      }: {
        data: HotTokensRowInfo;
        tokenStats: TokenStatsWebSocketShortMessage | undefined | null;
      }) => JSX.Element,
    },
    {
      label: (
        <div className="xxs:text-dex-white-secondary hover:text-dex-white">
          <Tooltip text="The current fully diluted Market Capitalization.">
            <div>Market Cap</div>
          </Tooltip>
        </div>
      ),
      key: 'MARKET_CAP',
      sortableKey: HotTokensSortKeys.MarketCap,
      component: MarketCapColumn as ({
        data,
        tokenStats,
      }: {
        data: HotTokensRowInfo;
        tokenStats: TokenStatsWebSocketShortMessage | undefined | null;
      }) => JSX.Element,
    },
    {
      label: (
        <div className="xxs:text-dex-white-secondary hover:text-dex-white">
          <Tooltip text="The current liquidity in the biggest pool.">
            <div>Liquidity</div>
          </Tooltip>
        </div>
      ),
      key: 'LIQUIDITY',
      sortableKey: HotTokensSortKeys.Liquidity,
      component: LiquidityColumn as ({
        data,
        tokenStats,
      }: {
        data: HotTokensRowInfo;
        tokenStats: TokenStatsWebSocketShortMessage | undefined | null;
      }) => JSX.Element,
    },
    {
      label: (
        <div className="xxs:text-dex-white-secondary hover:text-dex-white">
          <Tooltip text={`The date when the token was launched.`}>
            <div>Launch</div>
          </Tooltip>
        </div>
      ),
      key: 'CREATION_DATETIME',
      component: LaunchColumn as ({
        data,
        tokenStats,
      }: {
        data: HotTokensRowInfo;
        tokenStats: TokenStatsWebSocketShortMessage | undefined | null;
      }) => JSX.Element,
    },
    {
      label: <div className="xxs:text-dex-white-secondary" />,
      key: 'ACTIONS',
      component: ActionsColumn as ({
        data,
        tokenStats,
      }: {
        data: HotTokensRowInfo;
        tokenStats: TokenStatsWebSocketShortMessage | undefined | null;
      }) => JSX.Element,
    },
  ];
};

export const mobileColumns = (): HotTokensRowColumn<HotTokensRowInfo>[] => {
  return [
    {
      key: 'PAIR',
      component: MobileColumn as ({
        data,
        tokenStats,
      }: {
        data: HotTokensRowInfo;
        tokenStats: TokenStatsWebSocketShortMessage | undefined | null;
      }) => JSX.Element,
    },
  ];
};
